import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get stored session token
const getStoredToken = () => localStorage.getItem('session_token');
const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem('session_token', token);
  } else {
    localStorage.removeItem('session_token');
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check auth status on mount
  const checkAuth = useCallback(async () => {
    try {
      const storedToken = getStoredToken();
      const headers = storedToken 
        ? { 'Authorization': `Bearer ${storedToken}` }
        : {};
      
      const response = await fetch(`${API_URL}/api/users/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        setStoredToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login with email/password
  const login = async (email, password, betaCode = null) => {
    const body = { email, password };
    if (betaCode) {
      body.beta_code = betaCode;
    }
    
    // Use XMLHttpRequest to avoid any fetch interceptors
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/users/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            setUser(data.user);
            setStoredToken(data.session_token);
            resolve(data);
          } else {
            const errorCode = data.detail;
            
            // Handle specific error codes with friendly messages
            if (errorCode === 'NOT_REGISTERED') {
              reject(new Error('NOT_REGISTERED'));
            } else if (errorCode === 'INVALID_PASSWORD') {
              reject(new Error('INVALID_PASSWORD'));
            } else {
              reject(new Error(errorCode || 'Login failed'));
            }
          }
        } catch (e) {
          reject(new Error('Login failed'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send(JSON.stringify(body));
    });
  };

  // Register with email/password
  const register = async (name, email, password, betaCode = null) => {
    const body = { name, email, password };
    if (betaCode) {
      body.beta_code = betaCode;
    }
    
    const response = await fetch(`${API_URL}/api/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    setUser(data.user);
    setStoredToken(data.session_token);
    return data;
  };

  // Process Google OAuth session
  const processGoogleSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/users/google/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Google authentication failed');
    }

    const data = await response.json();
    setUser(data.user);
    // Store token in localStorage as backup for cookie issues
    setStoredToken(data.session_token);
    return data;
  };

  // Initiate Google login
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = (betaCode = null) => {
    // Store beta code in localStorage to apply after Google callback
    if (betaCode) {
      localStorage.setItem('pending_beta_code', betaCode.toUpperCase());
    } else {
      localStorage.removeItem('pending_beta_code');
    }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Logout
  const logout = async () => {
    try {
      const storedToken = getStoredToken();
      await fetch(`${API_URL}/api/users/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setStoredToken(null);
  };

  // Set user directly (for callback)
  const setUserData = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    initialized,
    login,
    register,
    loginWithGoogle,
    processGoogleSession,
    logout,
    setUserData,
    checkAuth,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
